import React, { useState } from 'react';
import { User, Upload, ImageOff, Plus, Loader2, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { resolveFileUrl } from '../../utils/fileUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocImage = ({ url, label, onImageClick, onDelete }) => {
  const [broken, setBroken] = useState(false);
  const resolved = resolveFileUrl(url);

  if (broken) {
    return (
      <div className="relative bg-[#1A1A1A] border border-[#27272A] p-6 flex flex-col items-center justify-center gap-2 min-h-[120px]">
        <div className="absolute top-2 left-2 bg-black/80 text-white text-xs px-2 py-1 uppercase font-mono">{label}</div>
        <ImageOff className="w-8 h-8 text-[#71717A]" />
        <p className="text-[#71717A] text-xs text-center">Immagine non disponibile</p>
        {onDelete && (
          <button
            onClick={onDelete}
            className="absolute top-2 right-2 bg-black/70 hover:bg-[#E3000F] text-white p-1 transition-colors"
            title={`Rimuovi foto ${label.toLowerCase()}`}
            data-testid={`delete-id-${label.toLowerCase()}-btn`}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute top-2 left-2 bg-black/80 text-white text-xs px-2 py-1 uppercase font-mono z-10">{label}</div>
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-2 right-2 bg-black/70 hover:bg-[#E3000F] text-white p-1 z-10 transition-colors opacity-80 hover:opacity-100"
          title={`Rimuovi foto ${label.toLowerCase()}`}
          data-testid={`delete-id-${label.toLowerCase()}-btn`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
      <img
        src={resolved}
        alt={label}
        className="w-full cursor-pointer border border-[#27272A] hover:border-[#E3000F] transition-colors"
        onClick={() => onImageClick(resolved)}
        onError={() => setBroken(true)}
        data-testid={`id-doc-${label.toLowerCase()}`}
      />
    </div>
  );
};

const UploadButton = ({ label, onUpload, loading }) => (
  <label className={`cursor-pointer flex items-center justify-center gap-2 bg-[#1A1A1A] border border-dashed border-[#444] hover:border-[#E3000F] text-[#A1A1AA] text-xs px-3 py-2 transition-colors ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
    {loading ? 'Lettura OCR...' : label}
    <input type="file" accept="image/*" className="hidden" disabled={loading}
      onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
  </label>
);

const PersonaDoc = ({ persona, pIdx, interventionId, onRefresh, onImageClick, totalPersone }) => {
  const [loadingFronte, setLoadingFronte] = useState(false);
  const [loadingRetro, setLoadingRetro] = useState(false);

  const uploadSide = async (side, file) => {
    const setter = side === 'fronte' ? setLoadingFronte : setLoadingRetro;
    setter(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(
        `${API_URL}/api/interventions/${interventionId}/id-photo/${pIdx}/${side}`,
        formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (onRefresh) onRefresh();
    } catch { /* noop */ }
    finally { setter(false); }
  };

  const deletePhoto = async (side) => {
    if (!window.confirm(`Eliminare la foto ${side} di ${persona.cognome || ''} ${persona.nome || 'questa persona'}?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/interventions/${interventionId}/persone/${pIdx}/photo?side=${side}`,
        { withCredentials: true }
      );
      if (onRefresh) onRefresh();
    } catch { /* noop */ }
  };

  const deletePersona = async () => {
    const name = `${persona.cognome || ''} ${persona.nome || ''}`.trim() || 'questa persona';
    if (!window.confirm(`Eliminare definitivamente ${name} dall'intervento?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/interventions/${interventionId}/persone/${pIdx}`,
        { withCredentials: true }
      );
      if (onRefresh) onRefresh();
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-2 pb-3 border-b border-[#27272A] last:border-b-0 last:pb-0">
      <div className="flex items-center justify-between">
        {totalPersone > 1 ? (
          <p className="text-[#A1A1AA] text-xs uppercase font-mono tracking-wider">{persona.cognome} {persona.nome}</p>
        ) : <span />}
        <button
          onClick={deletePersona}
          className="text-[#71717A] hover:text-[#E3000F] transition-colors p-1"
          title="Elimina questa persona"
          data-testid={`delete-persona-${pIdx}-btn`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          {persona.foto_documento_url ? (
            <DocImage url={persona.foto_documento_url} label="Fronte" onImageClick={onImageClick} onDelete={() => deletePhoto('fronte')} />
          ) : (
            <div className="bg-[#1A1A1A] border border-[#27272A] p-4 flex flex-col items-center justify-center gap-1 min-h-[80px]">
              <ImageOff className="w-6 h-6 text-[#52525B]" />
              <p className="text-[#52525B] text-[10px]">Fronte</p>
            </div>
          )}
          <UploadButton label="Fronte" onUpload={(f) => uploadSide('fronte', f)} loading={loadingFronte} />
        </div>
        <div className="space-y-1">
          {persona.foto_documento_retro_url ? (
            <DocImage url={persona.foto_documento_retro_url} label="Retro" onImageClick={onImageClick} onDelete={() => deletePhoto('retro')} />
          ) : (
            <div className="bg-[#1A1A1A] border border-[#27272A] p-4 flex flex-col items-center justify-center gap-1 min-h-[80px]">
              <ImageOff className="w-6 h-6 text-[#52525B]" />
              <p className="text-[#52525B] text-[10px]">Retro</p>
            </div>
          )}
          <UploadButton label="Retro" onUpload={(f) => uploadSide('retro', f)} loading={loadingRetro} />
        </div>
      </div>
    </div>
  );
};

export const IDDocumentsSidebar = ({ persone, interventionId, onImageClick, onRefresh }) => {
  const [addingNew, setAddingNew] = useState(false);

  const handleAddNewPerson = async (file) => {
    setAddingNew(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(
        `${API_URL}/api/interventions/${interventionId}/id-photo-new`,
        formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (onRefresh) onRefresh();
    } catch { /* noop */ }
    finally { setAddingNew(false); }
  };

  return (
    <div className="bg-[#121212] border border-[#27272A] p-6" data-testid="id-documents-sidebar">
      <h3 className="text-lg font-semibold uppercase mb-4 flex items-center gap-2 text-[#E3000F]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        <User className="w-5 h-5" />
        Documenti Identità
      </h3>

      {persone?.length > 0 ? (
        <div className="space-y-4">
          {persone.map((persona, pIdx) => (
            <PersonaDoc
              key={`persona-${pIdx}`}
              persona={persona}
              pIdx={pIdx}
              interventionId={interventionId}
              onRefresh={onRefresh}
              onImageClick={onImageClick}
              totalPersone={persone.length}
            />
          ))}
        </div>
      ) : (
        <p className="text-[#71717A] text-center text-sm py-2">Nessuna persona identificata</p>
      )}

      <div className="mt-4 pt-3 border-t border-[#27272A]">
        <label className={`cursor-pointer flex items-center justify-center gap-2 bg-[#1A1A1A] border border-[#E3000F]/30 hover:border-[#E3000F] text-[#E3000F] text-xs px-3 py-2.5 transition-colors ${addingNew ? 'opacity-50 pointer-events-none' : ''}`}
          data-testid="add-new-person-btn">
          {addingNew ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {addingNew ? 'Lettura OCR documento...' : 'Aggiungi persona (carica documento)'}
          <input type="file" accept="image/*" className="hidden" disabled={addingNew}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAddNewPerson(f); e.target.value = ''; }} />
        </label>
      </div>
    </div>
  );
};
